import { TTS_CHUNK_MAX_UTF8_BYTES, TTS_CHUNK_SIZE } from '../constants/readerSelectionToolLimits.js';

const CHUNK_BREAK_PUNCTUATION = '.;:,';
const NON_WHITESPACE_CHARACTER_PATTERN = /\S/;

/**
 * UTF-8 range boundaries: the first code point that needs 2, 3 and 4 bytes.
 * Everything below FIRST_TWO_BYTE_CODE_POINT is ASCII and weighs 1 byte.
 */
const FIRST_TWO_BYTE_CODE_POINT = 0x80;
const FIRST_THREE_BYTE_CODE_POINT = 0x800;
const FIRST_FOUR_BYTE_CODE_POINT = 0x10000;

/**
 * Smallest limit the pattern can express. The punctuation and whitespace
 * alternatives spend one character on the break itself, so a limit below 2 would
 * build an invalid `{1,0}` quantifier.
 */
const MINIMUM_CHUNK_LENGTH = 2;

/**
 * Builds the pattern that matches the next chunk — at most `maxChunkLength` code
 * points — at the start of the text. The caller applies it repeatedly, slicing off
 * each match:
 *
 *   'Hola mundo. Chau amigos' with a limit of 12 first matches 'Hola mundo.'
 *
 * Four alternatives, tried in order at each position — the order is the preference for
 * where to cut. Every one of them starts with `[\s\S]`, which matches any character:
 *
 *   1. `{1,limit}$`                                what is left fits: take all of it
 *   2. `{1,beforeBreak}[CHUNK_BREAK_PUNCTUATION]`  cut just after a punctuation mark
 *   3. `{1,beforeBreak}\s`                         cut just after a space, keeping words whole
 *   4. `{1,limit}`                                 nothing to cut on: take exactly the limit
 *
 * `{1,n}` takes as many characters as it can, so 2 and 3 cut at the LAST punctuation or
 * space within the limit. Option 4 requires nothing but a character being there, so a
 * match always exists at the start of any non-empty text and the caller's loop covers
 * the text with no gaps. `u` makes `[\s\S]` take whole code points, so an emoji never
 * splits and the length equals the `mb_strlen` the backend checks.
 * @param {number} maxChunkLength - Maximum code points per chunk
 * @returns {RegExp} Pattern matching the next chunk at the start of the text
 */
function buildChunkPattern(maxChunkLength) {
    const limit = Math.max(MINIMUM_CHUNK_LENGTH, maxChunkLength);
    const beforeBreak = limit - 1;

    return new RegExp(
        `[\\s\\S]{1,${limit}}$` +
            `|[\\s\\S]{1,${beforeBreak}}[${CHUNK_BREAK_PUNCTUATION}]` +
            `|[\\s\\S]{1,${beforeBreak}}\\s` +
            `|[\\s\\S]{1,${limit}}`,
        'u'
    );
}

/**
 * Measures how many bytes a code point takes once encoded as UTF-8, matching
 * TextEncoder's output, lone surrogates included.
 * @param {number} codePoint - Unicode code point
 * @returns {number} UTF-8 byte width, 1 to 4
 */
function countUtf8BytesOfCodePoint(codePoint) {
    if (codePoint < FIRST_TWO_BYTE_CODE_POINT) {
        return 1;
    }
    if (codePoint < FIRST_THREE_BYTE_CODE_POINT) {
        return 2;
    }
    if (codePoint < FIRST_FOUR_BYTE_CODE_POINT) {
        return 3;
    }

    return 4;
}

/**
 * Counts how many leading code points of the text fit both limits. Since every
 * shorter prefix weighs less, any cut inside this prefix also fits the budget.
 * @param {string} text - Text still pending to chunk
 * @param {number} maxCodePoints - Maximum code points per chunk
 * @param {number} maxBytes - Maximum UTF-8 bytes per chunk
 * @returns {number} Code points of the longest fitting prefix
 */
function countCodePointsWithinBudget(text, maxCodePoints, maxBytes) {
    let usedBytes = 0;
    let codePointCount = 0;

    for (const character of text) {
        const characterBytes = countUtf8BytesOfCodePoint(character.codePointAt(0));
        if (codePointCount >= maxCodePoints || usedBytes + characterBytes > maxBytes) {
            break;
        }
        usedBytes += characterBytes;
        codePointCount += 1;
    }

    return codePointCount;
}

/**
 * Splits text into chunks small enough for the TTS endpoint, each ending on a
 * natural pause where one is available. Chunks respect both the character limit
 * and the UTF-8 byte budget the encrypted payload allows. Keeps all the text,
 * except for fragments with nothing to synthesize.
 * @param {string} text - Text to read out loud
 * @param {number} [maxChunkLength=TTS_CHUNK_SIZE] - Maximum code points per chunk
 * @param {number} [maxChunkBytes=TTS_CHUNK_MAX_UTF8_BYTES] - Maximum UTF-8 bytes per chunk
 * @returns {string[]} Chunks in reading order, empty when there is nothing to read
 */
export default function splitTextIntoSpeechChunks(
    text,
    maxChunkLength = TTS_CHUNK_SIZE,
    maxChunkBytes = TTS_CHUNK_MAX_UTF8_BYTES
) {
    const chunks = [];
    let remainingText = text || '';

    while (remainingText.length > 0) {
        const fittingCodePoints = countCodePointsWithinBudget(remainingText, maxChunkLength, maxChunkBytes);
        const [chunk] = remainingText.match(buildChunkPattern(fittingCodePoints));

        chunks.push(chunk);
        remainingText = remainingText.slice(chunk.length);
    }

    return chunks.filter(chunk => NON_WHITESPACE_CHARACTER_PATTERN.test(chunk));
}
